import { useEffect, useState } from "react";
import { Route, Routes, Navigate } from "react-router-dom";
import { useMsal, useIsAuthenticated, useAccount } from "@azure/msal-react";
import ExpertsView from "./components/experts/ExpertsView";
import ReferencesView from "./components/references/ReferencesView";
import LandingPage from './components/landing/LandingPage';
import ToolbarNavigation from "./components/Navigation/ToolbarNavigation";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { createContext, useContext } from 'react';
import { themeINTEE, themeINTFRA } from "./utils/theme";
import { ThemeProvider } from '@mui/material/styles';
import PartnersView from "./components/partners/PartnersView";
import ProjectsView from "./components/projects/ProjectsView";
import ProjectControllingView from "./components/projects/ProjectControllingView";
import TimesheetView from "./components/timesheet/TimesheetView";
import { Backdrop, Fade } from "@mui/material";
import CVEvaluatorView from "./components/experts/evaluator/CVEvaluatorView";
import CVFormaterView from "./components/experts/evaluator/CVFormaterView";
import { ensureMsalInitialized } from "./services/auth.service";
import { NotificationProvider } from "./components/Shared/NotificationProvider";

// Protected Route Component
const ProtectedRoute = ({ 
  children, 
  requiredRoles, 
  isAuthenticated, 
  userRoles 
}: { 
  children: React.ReactNode; 
  requiredRoles: string[]; 
  isAuthenticated: boolean; 
  userRoles: string[]; 
}) => {
  const hasAccess = isAuthenticated && requiredRoles.some(role => userRoles.includes(role));

  if (!hasAccess) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

const ThemeContext = createContext(themeINTEE);
export const useTheme = () => useContext(ThemeContext);

function App() {
  const [, setAccessToken] = useState("");
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  const [, setUsername] = useState("");
  const isAuthenticated = useIsAuthenticated();
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [theme, setTheme] = useState(themeINTEE);
  const [isLoading, setIsLoading] = useState(true);

  // Timeout to prevent infinite loading
  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsLoading(false);
    }, 5000);

    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    let isCancelled = false;

    const hydrateAuthState = async () => {
      await ensureMsalInitialized();

    if (isAuthenticated && account) {
      setUsername(account.username);

      const scopes = ["User.Read", "Files.ReadWrite.All", 'Sites.ReadWrite.All', 'Sites.Manage.All'];

      instance.setActiveAccount(account);
      instance
        .acquireTokenSilent({
          scopes: scopes,
          account: account,
        })
        .then((response) => {
          if (isCancelled) return;
          setAccessToken(response.accessToken);
          
          // Extract roles from token claims
          const claims = response.idTokenClaims as any;
          let roles: string[] = [];
          
          if (claims?.roles) {
            roles = Array.isArray(claims.roles) ? claims.roles : [claims.roles];
          } else if (claims?.['http://schemas.microsoft.com/ws/2008/06/identity/claims/role']) {
            const roleClaim = claims['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
            roles = Array.isArray(roleClaim) ? roleClaim : [roleClaim];
          } else if (claims?.extension_Roles) {
            roles = Array.isArray(claims.extension_Roles) ? claims.extension_Roles : [claims.extension_Roles];
          }
          
          setUserRoles(roles);
          localStorage.setItem('accessToken', response.accessToken);

          // Set theme based on user roles
          if (roles.includes('INTEE') || roles.includes('AdminINTEE')) {
            setTheme(themeINTEE);
          } else if (roles.includes('INTFra') || roles.includes('AdminINTFra')) {
            setTheme(themeINTFRA);
          }
          
          setIsLoading(false);
        })
        .catch((error) => {
          if (isCancelled) return;
          console.error("Failed to acquire token silently:", error);
          setIsLoading(false);
          
          if (error instanceof InteractionRequiredAuthError) {
            instance
              .acquireTokenRedirect({
                scopes: scopes,
              })
              .catch((error) => {
                console.error("Failed to acquire token interactively:", error);
              });
          }
        });
    } else if (isAuthenticated === false && account === null) {
      setIsLoading(false);
    }
    };

    hydrateAuthState().catch((error) => {
      if (isCancelled) return;
      console.error("Failed to initialize authentication:", error);
      setIsLoading(false);
    });

    return () => {
      isCancelled = true;
    };
  }, [isAuthenticated, account, instance]);

  // Show loading while authentication is being processed
  if (isLoading) {
    return (
      <Backdrop open={isLoading} style={{ zIndex: 9999, color: '#fff' }}>
        <Fade in={isLoading}>
          <img src={theme.logo} alt="Company Logo" />
        </Fade>
      </Backdrop>
    );
  }

  return (
    <div className="App">
      <ThemeContext.Provider value={theme}>
        <ThemeProvider theme={theme}>
        <NotificationProvider>
          <ToolbarNavigation user={account?.username || ""} userRoles={userRoles} />
          <Routes>
            <Route path="/" element={<LandingPage isAuthenticated={isAuthenticated} userRole={userRoles[0]} />} />
            
            <Route 
              path="/references" 
              element={
                <ProtectedRoute 
                  isAuthenticated={isAuthenticated} 
                  userRoles={userRoles} 
                  requiredRoles={['INTEE', 'INTFra', 'AdminINTFra', 'AdminINTEE']}
                >
                  <ReferencesView userRoles={userRoles}/> 
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/references/:projectNumber" 
              element={
                <ProtectedRoute 
                  isAuthenticated={isAuthenticated} 
                  userRoles={userRoles} 
                  requiredRoles={['INTEE', 'INTFra', 'AdminINTFra', 'AdminINTEE']}
                >
                  <ReferencesView userRoles={userRoles}/> 
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/experts" 
              element={
                <ProtectedRoute 
                  isAuthenticated={isAuthenticated} 
                  userRoles={userRoles} 
                  requiredRoles={['INTEE', 'AdminINTEE']}
                >
                  <ExpertsView userRoles={userRoles}/> 
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/experts/:expertID" 
              element={
                <ProtectedRoute 
                  isAuthenticated={isAuthenticated} 
                  userRoles={userRoles} 
                  requiredRoles={['INTEE', 'AdminINTEE']}
                >
                  <ExpertsView userRoles={userRoles}/> 
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/partners" 
              element={
                <ProtectedRoute 
                  isAuthenticated={isAuthenticated} 
                  userRoles={userRoles} 
                  requiredRoles={['INTEE', 'AdminINTEE']}
                >
                  <PartnersView userRoles={userRoles}/> 
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/partners/:partnerID" 
              element={
                <ProtectedRoute 
                  isAuthenticated={isAuthenticated} 
                  userRoles={userRoles} 
                  requiredRoles={['INTEE', 'AdminINTEE']}
                >
                  <PartnersView userRoles={userRoles}/> 
                </ProtectedRoute>
              } 
            />
            <Route
              path="/projects"
              element={
                <ProtectedRoute
                  isAuthenticated={isAuthenticated}
                  userRoles={userRoles}
                  requiredRoles={['INTEE', 'AdminINTEE']}
                >
                  <ProjectsView userRoles={userRoles}/>
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects/:projectId/controlling"
              element={
                <ProtectedRoute
                  isAuthenticated={isAuthenticated}
                  userRoles={userRoles}
                  requiredRoles={['INTEE', 'AdminINTEE']}
                >
                  <ProjectControllingView />
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects/:projectId"
              element={
                <ProtectedRoute
                  isAuthenticated={isAuthenticated}
                  userRoles={userRoles}
                  requiredRoles={['INTEE', 'AdminINTEE']}
                >
                  <ProjectsView userRoles={userRoles}/>
                </ProtectedRoute>
              }
            />
            <Route
              path="/timesheet"
              element={
                <ProtectedRoute
                  isAuthenticated={isAuthenticated}
                  userRoles={userRoles}
                  requiredRoles={['INTEE', 'AdminINTEE']}
                >
                  <TimesheetView />
                </ProtectedRoute>
              }
            />
            <Route 
              path="/cvevaluator" 
              element={
                <ProtectedRoute 
                  isAuthenticated={isAuthenticated} 
                  userRoles={userRoles} 
                  requiredRoles={['INTEE', 'AdminINTEE','INTFra', 'AdminINTFra']}
                >
                  <CVEvaluatorView  /> 
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/cvformater" 
              element={
                <ProtectedRoute 
                  isAuthenticated={isAuthenticated} 
                  userRoles={userRoles} 
                  requiredRoles={['INTEE', 'AdminINTEE','INTFra', 'AdminINTFra']}
                >
                  <CVFormaterView />
                </ProtectedRoute>
              } 
            />
          </Routes>
        </NotificationProvider>
        </ThemeProvider>
      </ThemeContext.Provider>
    </div>
  );
}

export default App;